// ✅ Trekkin.app — useLogsStore.jsx v1.1.2 (FINAL DEFINITIVO)
// ------------------------------------------------------------------------
// ✔ Sanitização profunda (nunca tenta aceder .value)
// ✔ Logs leves e seguros (error, warn, chat)
// ✔ Limite máximo: 300 entradas
// ✔ Export TXT 100% compatível com iOS / WKWebView
// ✔ ZERO risco de erros em bundle minificado
// ------------------------------------------------------------------------

import { create } from "zustand";
import { ref, set as setDb } from "firebase/database";
import { translateNow } from "../i18n/translateNow";
import { db } from "../firebaseConfig";
import { useAuthStore } from "./useAuthStore";

const STORAGE_KEY = "trekkin.logs";
const REMOTE_QUEUE_KEY = "trekkin.logs.queue";
const LEVELS_KEY = "trekkin.logs.levels";
const LOGS_ENABLED_KEY = "trekkin.logs.enabled";
const MAX_LOGS = 300;
const MAX_REMOTE_QUEUE = 300;
const DEFAULT_LOGS_ENABLED = false;
const DEFAULT_LEVELS = {
  error: true,
  warn: true,
  chat: true,
  system: true,
  gps: true,
  info: true,
};

const REMOTE_LEVELS = ["error", "warn", "chat", "system", "gps", "info"];
const CRITICAL_REMOTE_LEVELS = ["error", "warn"];
const VALID_LEVELS = new Set(REMOTE_LEVELS);

function levelFromTag(tag) {
  const t = String(tag || "").toLowerCase();
  if (!t) return "info";
  if (t.includes("error") || t.includes("fail") || t.includes("denied") || t.includes("warn")) return "warn";
  if (t.includes("chat")) return "chat";
  if (t.includes("gps")) return "gps";
  if (t.includes("system") || t.includes("android") || t.includes("ios")) return "system";
  return "info";
}

function normalizeLogInput(level, message, extra) {
  const rawLevel = String(level || "").trim();
  if (VALID_LEVELS.has(rawLevel)) {
    return {
      level: rawLevel,
      message: String(message || ""),
      extra,
    };
  }

  const mappedLevel = levelFromTag(rawLevel);
  const prefixedMessage = `[${rawLevel || "log"}] ${String(message || "")}`.trim();

  let nextExtra = extra;
  if (nextExtra && typeof nextExtra === "object" && !Array.isArray(nextExtra)) {
    nextExtra = { _tag: rawLevel || "log", ...nextExtra };
  } else if (nextExtra != null) {
    nextExtra = { _tag: rawLevel || "log", value: nextExtra };
  } else {
    nextExtra = { _tag: rawLevel || "log" };
  }

  return {
    level: mappedLevel,
    message: prefixedMessage,
    extra: nextExtra,
  };
}

// --------------------------------------------------------------
// 🛡 SAFE SANITIZER
// --------------------------------------------------------------
function sanitize(data, depth = 0) {
  if (depth > 3) return "[depth-limit]";
  if (data === null || data === undefined) return null;

  const t = typeof data;
  if (t === "string" || t === "number" || t === "boolean") return data;

  if (Array.isArray(data)) {
    return data.slice(0, 20).map((v) => sanitize(v, depth + 1));
  }

  if (t === "object" && data.constructor === Object) {
    const out = {};
    for (const k in data) {
      const val = data[k];
      if (typeof val === "function") out[k] = "[function]";
      else out[k] = sanitize(val, depth + 1);
    }
    return out;
  }

  return String(data);
}

// --------------------------------------------------------------
// 🔧 Safe stringify
// --------------------------------------------------------------
function safeStringifyLight(obj) {
  try {
    const clean = sanitize(obj);
    let out = JSON.stringify(clean);
    if (out.length > 400) out = out.slice(0, 400) + "...[truncated]";
    return out;
  } catch {
    return "[unserializable]";
  }
}

// --------------------------------------------------------------
// 🔁 Remote logs (Firebase)
// --------------------------------------------------------------
function shouldSendRemote(level) {
  try {
    if (!REMOTE_LEVELS.includes(level)) return false;
    const st = useAuthStore.getState();
    const user = st?.user;
    if (!user?.uid || !db) return false;
    if (user.logRemoteEnabled === false) return false;

    const levels = user.logRemoteLevels || {};
    return levels[level] !== false;
  } catch {
    return false;
  }
}

function loadRemoteQueue() {
  try {
    const parsed = JSON.parse(localStorage.getItem(REMOTE_QUEUE_KEY) || "[]");
    return Array.isArray(parsed) ? parsed.slice(-MAX_REMOTE_QUEUE) : [];
  } catch {
    return [];
  }
}

function saveRemoteQueue(queue) {
  try {
    localStorage.setItem(
      REMOTE_QUEUE_KEY,
      JSON.stringify(queue.slice(-MAX_REMOTE_QUEUE)),
    );
  } catch {}
}

function removeRemoteQueueItem(id) {
  saveRemoteQueue(loadRemoteQueue().filter((item) => item?.id !== id));
}

let remoteFlushPromise = null;

export function flushRemoteLogs() {
  if (remoteFlushPromise) return remoteFlushPromise;
  if (!db || (typeof navigator !== "undefined" && navigator.onLine === false)) {
    return Promise.resolve(false);
  }

  remoteFlushPromise = (async () => {
    const queue = loadRemoteQueue();
    for (const item of queue) {
      if (!item?.uid || !item?.dayKey || !item?.id || !item?.payload) {
        removeRemoteQueueItem(item?.id);
        continue;
      }
      try {
        await setDb(
          ref(db, `/logs/app/${item.uid}/${item.dayKey}/${item.id}`),
          item.payload,
        );
        removeRemoteQueueItem(item.id);
      } catch {
        return false;
      }
    }
    return true;
  })().finally(() => {
    remoteFlushPromise = null;
  });

  return remoteFlushPromise;
}

function sendRemoteLog(entry, rawExtra) {
  try {
    const st = useAuthStore.getState();
    const user = st?.user;
    if (!user?.uid || !db) return;

    const d = new Date(entry.ts || Date.now());
    const dayKey =
      d.getFullYear() +
      String(d.getMonth() + 1).padStart(2, "0") +
      String(d.getDate()).padStart(2, "0");

    const buildLabel =
      typeof window !== "undefined"
        ? window.__TREKKIN_BUILD_INFO?.label || null
        : null;

    const payload = {
      createdAt: entry.ts || Date.now(),
      device: {
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "unknown",
      },
      build: buildLabel,
      logs: [
        {
          ...entry,
          extraRaw: rawExtra ? sanitize(rawExtra) : null,
        },
      ],
      source: "remote",
    };

    const id = `log-${entry.ts || Date.now()}-${Math.random()
      .toString(36)
      .slice(2, 10)}`;
    saveRemoteQueue([
      ...loadRemoteQueue(),
      { id, uid: user.uid, dayKey, payload },
    ]);
    void flushRemoteLogs();
  } catch {}
}

if (typeof window !== "undefined" && !window.__TREKKIN_LOG_QUEUE_LISTENER__) {
  window.__TREKKIN_LOG_QUEUE_LISTENER__ = true;
  window.addEventListener("online", () => {
    void flushRemoteLogs();
  });
  window.setTimeout(() => {
    void flushRemoteLogs();
  }, 5000);
}

// --------------------------------------------------------------
// 💾 Load / Save
// --------------------------------------------------------------
function loadLogs() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveLogs(arr) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(arr));
  } catch (e) {
    console.warn("⚠️ [LogsStore] Falha ao guardar logs:", e);
  }
}

function loadEnabledLevels() {
  try {
    const raw = localStorage.getItem(LEVELS_KEY);
    if (!raw) return { ...DEFAULT_LEVELS };
    const parsed = JSON.parse(raw);
    return { ...DEFAULT_LEVELS, ...(parsed || {}) };
  } catch {
    return { ...DEFAULT_LEVELS };
  }
}

function saveEnabledLevels(levels) {
  try {
    localStorage.setItem(LEVELS_KEY, JSON.stringify(levels));
  } catch (e) {
    console.warn("⚠️ [LogsStore] Falha ao guardar níveis:", e);
  }
}

function loadLogsEnabled() {
  try {
    const raw = localStorage.getItem(LOGS_ENABLED_KEY);
    if (raw === null) return DEFAULT_LOGS_ENABLED;
    return raw === "1" || raw === "true";
  } catch {
    return DEFAULT_LOGS_ENABLED;
  }
}

function saveLogsEnabled(enabled) {
  try {
    localStorage.setItem(LOGS_ENABLED_KEY, enabled ? "1" : "0");
  } catch (e) {
    console.warn("⚠️ [LogsStore] Falha ao guardar enabled:", e);
  }
}

// --------------------------------------------------------------
// 🧰 STORE
// --------------------------------------------------------------
export const useLogsStore = create((set, get) => ({
  logs: loadLogs(),
  filterLevel: "ALL",
  filterSearch: "",
  logsEnabled: loadLogsEnabled(),
  enabledLevels: loadEnabledLevels(),

  // --------------------------------------------------
  // ➕ ADD LOG
  // --------------------------------------------------
  addLog: (level, message, extra = null) => {
    const normalized = normalizeLogInput(level, message, extra);
    if (!get().logsEnabled) return;
    if (get().enabledLevels?.[normalized.level] === false) return;
    const entry = {
      ts: Date.now(),
      level: normalized.level,
      message: String(normalized.message || ""),
      extra: normalized.extra ? safeStringifyLight(normalized.extra) : null,
    };

    let arr = [entry, ...get().logs];
    if (arr.length > MAX_LOGS) arr = arr.slice(0, MAX_LOGS);

    saveLogs(arr);
    set({ logs: arr });

    if (shouldSendRemote(normalized.level)) {
      sendRemoteLog(entry, normalized.extra);
    }
  },

  setLogsEnabled: (enabled) => {
    const next = !!enabled;
    saveLogsEnabled(next);
    set({ logsEnabled: next });
  },

  // --------------------------------------------------
  // 🧹 CLEAR
  // --------------------------------------------------
  clearLogs: () => {
    localStorage.removeItem(STORAGE_KEY);
    set({ logs: [] });
  },

  setLogs: (arr) => {
    const next = Array.isArray(arr) ? arr.slice(0, MAX_LOGS) : [];
    saveLogs(next);
    set({ logs: next });
  },

  setFilterLevel: (lvl) => set({ filterLevel: lvl }),
  setFilterSearch: (txt) =>
    set({ filterSearch: (txt || "").toLowerCase() }),

  setEnabledLevel: (level, enabled) => {
    if (!DEFAULT_LEVELS.hasOwnProperty(level)) return;
    const next = { ...get().enabledLevels, [level]: !!enabled };
    saveEnabledLevels(next);
    set({ enabledLevels: next });
  },

  setAllEnabledLevels: (enabled) => {
    const next = Object.keys(DEFAULT_LEVELS).reduce((acc, k) => {
      acc[k] = !!enabled;
      return acc;
    }, {});
    saveEnabledLevels(next);
    set({ enabledLevels: next });
  },

  // --------------------------------------------------
  // 📤 EXPORT TXT — iOS / WKWebView SAFE (FINAL)
  // --------------------------------------------------
  exportLogs: async () => {
    const { logs } = get();

    if (!logs || logs.length === 0) {
      alert(translateNow("logs.alert.empty"));
      return;
    }

    const lines = logs.map((l) => {
      const time = new Date(l.ts).toLocaleTimeString("pt-PT");
      return `${time}  ${l.level.toUpperCase()}  ${l.message}${
        l.extra ? " " + l.extra : ""
      }`;
    });

    const txt = lines.join("\n");
    const isNative = window?.Capacitor?.isNativePlatform?.();

    if (isNative) {
      try {
        const fs = window?.Capacitor?.Plugins?.Filesystem;
        const share = window?.Capacitor?.Plugins?.Share;
        const browser = window?.Capacitor?.Plugins?.Browser;
        const toBase64 = (str) => btoa(unescape(encodeURIComponent(str)));

        if (!fs?.writeFile) {
          alert("Plugin Filesystem não disponível.");
          return;
        }

        const filename = "trekkin-logs.txt";
        const res = await fs.writeFile({
          path: filename,
          data: toBase64(txt),
          directory: "CACHE",
        });

        const fileUri = res?.uri;
        if (!fileUri) {
          alert(translateNow("logs.alert.exportError"));
          return;
        }

        const fileUrl =
          window?.Capacitor?.convertFileSrc?.(fileUri) || fileUri;

        if (share?.share) {
          await share.share({
            title: filename,
            text: filename,
            url: fileUrl,
          });
          return;
        }

        if (browser?.open) {
          await browser.open({ url: fileUrl });
          return;
        }
      } catch (e) {
        console.warn("⚠️ Erro ao exportar logs (native):", e);
      }
    }

    const blob = new Blob([txt], {
      type: "text/plain;charset=utf-8",
    });

    const url = URL.createObjectURL(blob);

    try {
      const a = document.createElement("a");
      a.href = url;
      a.download = "trekkin-logs.txt";
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (e) {
      console.warn("⚠️ Erro ao exportar logs:", e);
      alert(translateNow("logs.alert.exportError"));
    }

    setTimeout(() => {
      URL.revokeObjectURL(url);
    }, 3000);
  },
}));

// --------------------------------------------------------------
// 🔊 logEvent GLOBAL (SAFE)
// --------------------------------------------------------------
export const logEvent = (level, message, extra = null) => {
  try {
    useLogsStore.getState().addLog(level, message, extra);
  } catch (e) {
    console.warn("⚠️ logEvent falhou:", e);
  }
};
